// Stock checks in the basket and checkout (BUG-440). A line can go over stock after it was added
// (someone else bought the last one, or the basket is days old); the order API then refuses it.
// Checkout re-reads stock when it opens, marks such lines and blocks payment until they are fixed,
// and turns the API's refusal into a message that names the product in the page language.

import { ApiError } from './shopApi'

export interface StockLine {
  key: string
  sku: string
  quantity: number
}

/** Lines asking for more than is available, with what is available (0 = sold out). */
export function overStock(lines: StockLine[], stockBySku: Record<string, number>): Record<string, number> {
  const issues: Record<string, number> = {}
  for (const l of lines) {
    const available = Math.max(0, stockBySku[l.sku] ?? 0)
    if (l.quantity > available) issues[l.key] = available
  }
  return issues
}

export interface OutOfStock {
  sku: string
  available: number
}

/**
 * The order API's refusal for stock (webshop `OutOfStockException`: 400, code OUT_OF_STOCK, sku and
 * available), or null for any other error. The raw backend text is never shown to the customer.
 */
export function outOfStockOf(err: unknown): OutOfStock | null {
  if (!(err instanceof ApiError)) return null
  const p = err.problem as { code?: unknown; sku?: unknown; available?: unknown } | null
  if (!p || p.code !== 'OUT_OF_STOCK' || typeof p.sku !== 'string') return null
  return { sku: p.sku, available: typeof p.available === 'number' ? Math.max(0, p.available) : 0 }
}

/**
 * BUG-453: what live stock means for the basket. A line asking for more than is left (but some is)
 * is lowered to what is left; a line with nothing left is sold out and stays, greyed, for the
 * customer to remove. An SKU missing from the stock list is left alone (unknown is not sold out).
 */
export function reconcile(lines: StockLine[], stockBySku: Record<string, number>): { lower: Record<string, number>; soldOut: string[] } {
  const lower: Record<string, number> = {}
  const soldOut: string[] = []
  for (const l of lines) {
    if (!(l.sku in stockBySku)) continue
    const available = Math.max(0, stockBySku[l.sku])
    if (available === 0) soldOut.push(l.key)
    else if (l.quantity > available) lower[l.key] = available
  }
  return { lower, soldOut }
}
